import {
  Button,
  Css,
  IconButton,
  ModalProps,
  ResponsiveGrid,
  ResponsiveGridItem,
  useBreakpoint,
  useModal,
  useToast,
} from "@homebound/beam";
import { ObjectConfig, ObjectState, required, useFormState } from "@homebound/form-state";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { CommentFeed, PdfViewer } from "src/components";
import { Card } from "src/components/Card";
import {
  BillPage_BillFragment,
  BillPage_BillProjectStageFragment,
  BillPage_BillTradePartnerFragment,
  BillPage_CommitmentLineItemFragment,
  BillPage_DocumentFragment,
} from "src/fragments/circularFragments.generated";
import {
  BillStatus,
  BillType,
  SaveBillInput,
  SaveBillLineItemInput,
  SaveCommentInput,
} from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import {
  useBillPageQuery,
  useBillPage_DeleteBillMutation,
  useBillPage_SaveBillMutation,
} from "src/routes/bills/BillPage.generated";
import { addEntityParam, createBillPageUrl, createBillsAndCreditsPageUrl } from "src/RouteUrls";
import { queryResult } from "src/utils";
import { hasDraws, isDepositBill, isHeadlessBill } from "src/utils/bills";
import { Temporal } from "temporal-polyfill";
import { PageHeader } from "../layout/PageHeader";
import { IdOrAddParams } from "../routesDef";
import {
  BillActionConfirmationModal,
  BillActionConfirmationModalProps,
} from "./components/BillActionConfirmationModal";
import { BillBanner } from "./components/BillBanner";
import { BillHistory } from "./components/BillHistory";
import { BillLienWaiversDataTable } from "./components/BillLienWaiversDataTable";
import { BillLineItemsDataTable } from "./components/BillLineItemsDataTable";
import { BillSummary } from "./components/BillSummary";
import { getDynamicPdfVierwerWidth } from "./utils";

type BillPageProps = { billId?: string };

export function BillPage({ billId }: BillPageProps) {
  const { idOrAdd } = useRouteParams<IdOrAddParams>();
  // Prefer an explicit prop (e.g. from BillEditor); otherwise use `/bills/:idOrAdd`.
  billId ??= idOrAdd !== addEntityParam ? idOrAdd : undefined;

  const query = useBillPageQuery({ variables: { billId: billId! }, skip: !billId });
  if (!billId) {
    return <Navigate to={createBillsAndCreditsPageUrl()} replace />;
  }
  return queryResult(query, ({ bill }) => <BillPageView bill={bill} />);
}

const useGetBrowserWidth = () => {
  const [width, setWidth] = useState(window.innerWidth);
  const handleResize = () => setWidth(window.innerWidth);
  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return width;
};

type BillPageViewProps = { bill: BillPage_BillFragment };

function BillPageView({ bill }: BillPageViewProps) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { openModal } = useModal();
  const [deleteBill] = useBillPage_DeleteBillMutation();
  const [saveBill] = useBillPage_SaveBillMutation();
  const { sm, smOrMd } = useBreakpoint();

  const browserWidth = useGetBrowserWidth();
  const [showBillFile, setShowBillFile] = useState(false);

  const formState = useFormState({ config: formConfig, init: { input: bill, map: mapToForm }, readOnly: true });

  const hasLienWaivers = bill.lienWaivers.nonEmpty;
  const isReleasableBill = bill?.isDeferred && [BillStatus.Draft, BillStatus.Unreleased].includes(bill.status.code);

  const onDelete = useCallback(async () => {
    await deleteBill({ variables: { input: { id: bill.id } } });
    showToast({ message: `Bill #${bill?.tradePartnerNumber} successfully deleted`, type: "success" });
    navigate(createBillsAndCreditsPageUrl());
  }, [bill.id, bill?.tradePartnerNumber, deleteBill, navigate, showToast]);

  const onReverse = useCallback(
    async (comment?: SaveCommentInput) => {
      await saveBill({ variables: { input: { id: bill.id, status: BillStatus.Reversed, comment } } });
      showToast({ message: `Bill #${bill?.tradePartnerNumber} processing reversal...`, type: "info" });
    },
    [bill.id, bill?.tradePartnerNumber, saveBill, showToast],
  );

  const onCancel = useCallback(
    async (comment?: SaveCommentInput) => {
      await saveBill({ variables: { input: { id: bill.id, status: BillStatus.Cancelled, comment } } });
      showToast({ message: `Bill #${bill?.tradePartnerNumber} has been successfully cancelled`, type: "success" });
    },
    [bill.id, bill?.tradePartnerNumber, saveBill, showToast],
  );

  const onRelease = useCallback(async () => {
    const res = await saveBill({
      // Pass bill status as ReleaseScheduled when unreleased bills are released
      variables: { input: { id: bill.id, status: BillStatus.ReleaseScheduled } },
    });
    if (res.data?.saveBill.bill) {
      showToast({
        message: `Bill ${bill.tradePartnerNumber} sent to ${bill.tradePartner.name} for Approval`,
        type: "info",
      });
    }
  }, [bill.id, bill.tradePartnerNumber, bill.tradePartner.name, saveBill, showToast]);

  const PAGE_DESKTOP_WIDTH = 1800;
  const billDataSectionSize = browserWidth > PAGE_DESKTOP_WIDTH ? "60%" : "50%";
  const billFileSectionSize = browserWidth > PAGE_DESKTOP_WIDTH ? "40%" : "50%";

  return (
    <>
      <PageHeader
        title={bill.name}
        breadcrumb={{ href: createBillsAndCreditsPageUrl(), label: "Bills & Credits" }}
        right={
          <div css={Css.if(smOrMd).df.fdr.gap1.else.df.$}>
            {/* Delete/Cancel/Reverse */}
            <FinalBillFormActions {...{ bill, onDelete, onCancel, onReverse, openModal }} />
            {/* Edit */}
            {bill.canEdit.allowed && (
              <Button
                label="Edit"
                variant="secondary"
                onClick={() => navigate(createBillPageUrl({ idOrAdd: bill.id, edit: true }))}
              />
            )}
            {/* Release */}
            {isReleasableBill && (
              <div css={Css.if(sm).mx0.else.mx1.$}>
                <Button label="Release" onClick={onRelease} />
              </div>
            )}
          </div>
        }
      />

      {(bill.isBilled || bill.status.code === BillStatus.Reversed) && (
        <BillBanner billId={bill!.id} hasSyncError={Boolean(bill!.intacctSyncError)} />
      )}

      <ResponsiveGrid columns={20} minColumnWidth={40} gap={30}>
        <ResponsiveGridItem colSpan={1} />
        <ResponsiveGridItem colSpan={18}>
          <div css={Css.df.gapPx(20).$}>
            <div css={Css.w(showBillFile ? billDataSectionSize : "100%").pr2.$}>
              <div css={Css.df.fdc.rg3.$}>
                <BillSummary
                  formState={formState}
                  bill={bill}
                  showBillFile={showBillFile}
                  showBillFileCallback={() => setShowBillFile((show) => !show)}
                />
                <section css={Css.df.$}>
                  <div css={Css.w("100%").df.fdc.rg2.if(hasLienWaivers).w("60%").$}>
                    <h1 css={Css.pl1.md.$}>Line Items</h1>
                    <Card>
                      <BillLineItemsDataTable formState={formState} isHeadlessBill={isHeadlessBill(bill)} />
                    </Card>
                  </div>
                  {hasLienWaivers && (
                    <div css={Css.w("40%").df.fdc.rg2.pl4.$}>
                      <h1 css={Css.pl1.md.$}>Lien Waivers</h1>
                      <Card>
                        <BillLienWaiversDataTable
                          isLienWaiverReceiver={bill.tradePartner.isLienWaiverReceiver}
                          lienWaivers={bill.lienWaivers}
                        />
                      </Card>
                    </div>
                  )}
                </section>
                <section css={Css.df.cg3.$}>
                  <div css={Css.w("60%").df.fdc.rg2.$}>
                    <h1 css={Css.pl1.md.$}>History</h1>
                    <BillHistory historyItems={bill.history} />
                  </div>
                  <div css={Css.w("40%").$}>
                    <CommentFeed commentable={bill} />
                  </div>
                </section>
              </div>
            </div>
            {showBillFile && (
              <div css={Css.w(billFileSectionSize).pr2.$}>
                <div css={Css.bgWhite.bshBasic.br8.mhPx(400).oa.$}>
                  <div css={Css.df.jcfe.p1.$}>
                    <IconButton icon="x" onClick={() => setShowBillFile(false)} />
                  </div>
                  <PdfViewer
                    hasHeader
                    assets={formState.documents.value.map(({ asset }) => asset)}
                    handlePdfDelete={(assetId) =>
                      formState.documents.remove(formState.documents.value.findIndex((d) => d.asset.id === assetId))
                    }
                    pdfPageWidth={getDynamicPdfVierwerWidth(browserWidth)}
                  />
                </div>
              </div>
            )}
          </div>
        </ResponsiveGridItem>
        <ResponsiveGridItem colSpan={1} />
      </ResponsiveGrid>
    </>
  );
}

type FinalBillFormActionsProps = {
  bill: Pick<BillPage_BillFragment, "id" | "name" | "canCancel" | "canDelete" | "canReverse">;
  onDelete: () => Promise<void>;
  onReverse: () => Promise<void>;
  onCancel: () => Promise<void>;
  openModal: (props: ModalProps) => void;
};

type FinalActionButton = { [label: string]: Pick<BillActionConfirmationModalProps, "action" | "onConfirm"> };

export function FinalBillFormActions({ bill, onCancel, onDelete, onReverse, openModal }: FinalBillFormActionsProps) {
  // Get the final options (Delete, Reverse, Cancel) the user can take on the bill
  const finalActionButtons = useMemo<FinalActionButton>(() => {
    const { canDelete, canReverse, canCancel } = bill;
    return {
      ...(canDelete.allowed && { Delete: { onConfirm: onDelete, action: "delete" } }),
      ...(canReverse.allowed && { Reverse: { onConfirm: onReverse, action: "reverse" } }),
      ...(canCancel.allowed && { Cancel: { onConfirm: onCancel, action: "cancel" } }),
    };
  }, [bill, onCancel, onDelete, onReverse]);

  // Render the final action buttons
  return finalActionButtons.toEntries().map(([label, props]) => (
    <div css={Css.mr1.$} key={label}>
      <Button
        label={label}
        variant="danger"
        onClick={() => openModal({ content: <BillActionConfirmationModal bill={bill} {...props} /> })}
      />
    </div>
  ));
}

export type FormLineItem = Omit<SaveBillLineItemInput, "costCode"> &
  Pick<BillPage_CommitmentLineItemFragment, "costChangeInCents"> & {
    unbilledInCents?: number;
    pendingBilledInCents?: number;
    pendingUnbilledInCents?: number;
    billableLimitInCents?: number;
    unbilledCommittedInCents?: number;
    uncommittedBudgetAmountInCents?: number;
    revisedApprovedBudgetInCents?: number;
    taxableAmountInCents?: number;
    isTaxExempt?: boolean;
    isPendingOrBilled?: boolean;
    displayName: string;
    bliId?: string;
    otherBillInCents?: number | null;
    owner?: { __typename?: "Commitment" | "CommitmentChangeOrder" | undefined; id: string; accountingNumber: number };
    changeOrdersWithSameProjectItem?: {
      id: string;
      costChangeInCents?: number | null | undefined;
      projectItem: { id: string };
    }[];
    drawItems?: Omit<FormLineItem, "drawItems">[];
  };

export type FormInput = Omit<
  SaveBillInput,
  "tradePartnerId" | "balanceInCents" | "documents" | "quickbooksId" | "lineItems" | "dueDate" | "billDate"
> & {
  name: string;
  documents: BillPage_DocumentFragment[];
  dueDate?: Temporal.PlainDate | null;
  billDate?: Temporal.PlainDate | null;
  paidInCents?: number | null;
  billedInCents?: number | null;
  type: BillType;
  lineItems?: FormLineItem[];
  intacctId?: string | null;
  tradePartner?: BillPage_BillTradePartnerFragment;
  projectStage?: BillPage_BillProjectStageFragment;
};

const lineItemConfig: ObjectConfig<Omit<FormLineItem, "drawItems">> = {
  id: { type: "value" },
  bliId: { type: "value", readOnly: true },
  displayName: { type: "value" },
  costChangeInCents: { type: "value" },
  commitmentLineItemId: { type: "value" },
  projectItemId: { type: "value" },
  amountInCents: { type: "value" },
  pendingBilledInCents: { type: "value" },
  billableLimitInCents: { type: "value" },
  taxableAmountInCents: { type: "value" },
  isTaxExempt: { type: "value" },
  isPendingOrBilled: { type: "value" },
  unbilledInCents: { type: "value" },
  revisedApprovedBudgetInCents: { type: "value" },
  uncommittedBudgetAmountInCents: { type: "value" },
  otherBillInCents: { type: "value" },
  owner: { type: "object", config: { id: { type: "value" }, accountingNumber: { type: "value" } } },
};

export const formConfig: ObjectConfig<FormInput> = {
  id: { type: "value" },
  name: { type: "value" },
  billDate: { type: "value", rules: [required] },
  dueDate: { type: "value" },
  postedDate: { type: "value" },
  paidDate: { type: "value" },
  billedInCents: { type: "value" },
  paidInCents: { type: "value" },
  internalNote: { type: "value" },
  type: { type: "value" },
  intacctId: { type: "value" },
  isTradePartnerCredit: { type: "value" },
  lineItems: { type: "list", config: { ...lineItemConfig, drawItems: { type: "list", config: lineItemConfig } } },
  tradePartner: {
    type: "object",
    config: {
      id: { type: "value" },
      name: { type: "value" },
      isLienWaiverReceiver: { type: "value" },
      paymentTerm: {
        type: "object",
        config: {
          id: { type: "value" },
          dueDay: { type: "value" },
          alignWithPaymentCycle: { type: "value" },
          dueFrom: { type: "object", config: { code: { type: "value" }, name: { type: "value" } } },
        },
      },
    },
  },
  projectStage: {
    type: "object",
    config: {
      id: { type: "value" },
      stage: { type: "object", config: { code: { type: "value" } } },
      project: { type: "object", config: { id: { type: "value" }, name: { type: "value" } } },
    },
  },
  documents: {
    type: "list",
    rules: [required],
    config: {
      id: { type: "value" },
      name: { type: "value" },
      asset: {
        type: "object",
        config: {
          id: { type: "value" },
          attachmentUrl: { type: "value" },
          contentType: { type: "value" },
          createdAt: { type: "value" },
          downloadUrl: { type: "value" },
          version: { type: "value" },
        },
      },
    },
  },
};

function mapToForm(bill: BillPage_BillFragment): FormInput {
  const { type, source, status, lineItems, ...others } = bill;
  const formLis: FormLineItem[] = [];
  if (isHeadlessBill(bill)) {
    formLis.push(
      ...(lineItems?.map((li) => ({
        id: li.id,
        amountInCents: li.amountInCents,
        projectItem: li.projectItem,
        bliId: li.id,
        displayName: li.projectItem.displayName,
        pendingBilledInCents: 0,
        billableLimitInCents: 0,
        isPendingOrBilled: bill.isPendingOrBilled,
        costChangeInCents: 0,
        projectItemId: li.projectItem.id,
        unbilledInCents: Number(li.projectItem.uncommittedBudgetAmountInCents),
        uncommittedBudgetAmountInCents: li.projectItem.uncommittedBudgetAmountInCents,
        revisedApprovedBudgetInCents: li.projectItem.revisedApprovedBudgetInCents,
        taxableAmountInCents: li.taxableAmountInCents,
        isTaxExempt: li.isTaxExempt,
      })) ?? []),
    );
  } else if (hasDraws(bill)) {
    const [drawLineItems, nonDrawLineItems] = lineItems.partition((li) => !!li.commitmentDraw);
    // First group by commitment draw
    const drawGroups = drawLineItems.uniqueBy((li) => li.commitmentDraw!.id);
    drawGroups.forEach(({ commitmentDraw }) => {
      const bs = commitmentDraw!.commitment?.billingSchedule?.find((d) => d.commitmentDraw?.id === commitmentDraw!.id)!;
      const drawItems = bs.lineItems;
      const costCodeGroups = drawItems.groupBy((li) => li.commitmentLineItem!.costCode!.id);
      // Then we create the lineItem for the draw
      formLis.push({
        id: commitmentDraw!.id,
        commitmentLineItemId: commitmentDraw!.id,
        amountInCents: drawItems.sum((di) => di.amountInCents),
        pendingBilledInCents: bs.pendingBilledInCents,
        isPendingOrBilled: bill.isPendingOrBilled,
        costChangeInCents: bs.committedInCents,
        owner: commitmentDraw!.commitment,
        displayName: `(Draw) ${commitmentDraw!.task.name}`,
        otherBillInCents: 0,
        unbilledInCents: 0,
        commitmentDrawId: commitmentDraw!.id,
        // And for each draw we show the sub items by cost codes
        drawItems: costCodeGroups
          .toEntries()
          .map(([costCodeId, costCodeItems]) => ({
            id: costCodeId,
            commitmentLineItemId: costCodeId,
            isPendingOrBilled: bill.isPendingOrBilled,
            owner: commitmentDraw!.commitment,
            displayName: costCodeItems.first!.commitmentLineItem!.costCode!.displayName ?? "",
            otherBillInCents: 0,
            commitmentDrawId: commitmentDraw!.id,
            costChangeInCents: costCodeItems.sum((li) => li.amountInCents),
          }))
          .sortBy((li) => li.displayName),
      });
    });
    if (nonDrawLineItems.nonEmpty)
      formLis.push(
        ...nonDrawLineItems.map((li) => ({
          id: li.id,
          commitmentLineItemId: li.commitmentLineItem?.id,
          amountInCents: li.amountInCents,
          pendingBilledInCents: li.commitmentLineItem?.pendingBilledInCents,
          billableLimitInCents: li.commitmentLineItem?.billableLimitInCents,
          isPendingOrBilled: bill.isPendingOrBilled,
          costChangeInCents: li.commitmentLineItem?.costChangeInCents,
          owner: li.commitmentLineItem?.owner,
          displayName: li.commitmentLineItem?.projectItem.displayName ?? "",
          unbilledInCents: li.commitmentLineItem?.unbilledInCents ?? 0,
          otherBillInCents: 0,
          projectItemId: li.projectItem.id,
          taxableAmountInCents: li.taxableAmountInCents,
          isTaxExempt: li.isTaxExempt,
        })),
      );
  } else if (isDepositBill(bill) && lineItems.nonEmpty) {
    const costChangeInCents = lineItems.sum((li) => li.commitmentLineItem?.costChangeInCents ?? 0);
    const amountInCents = lineItems.sum((li) => li.amountInCents);
    const depositInPercentage = (amountInCents / costChangeInCents) * 100;

    formLis.push({
      id: lineItems.first!.id,
      commitmentLineItemId: lineItems.first!.commitmentLineItem?.id,
      amountInCents,
      pendingBilledInCents: lineItems.sum((li) => li.commitmentLineItem?.pendingBilledInCents),
      billableLimitInCents: lineItems.sum((li) => li.commitmentLineItem?.billableLimitInCents),
      isPendingOrBilled: bill.isPendingOrBilled,
      costChangeInCents,
      owner: lineItems.first!.commitmentLineItem?.owner,
      displayName: `${depositInPercentage}% Deposit`,
      unbilledInCents: lineItems.sum((li) => li.commitmentLineItem?.unbilledInCents),
      otherBillInCents: 0,
      projectItemId: lineItems.first!.projectItem.id,
    });
  } else {
    formLis.push(
      ...(lineItems?.map((li) => ({
        id: li.id,
        commitmentLineItemId: li.commitmentLineItem?.id,
        amountInCents: li.amountInCents,
        pendingBilledInCents: li.commitmentLineItem?.pendingBilledInCents,
        billableLimitInCents: li.commitmentLineItem?.billableLimitInCents,
        isPendingOrBilled: bill.isPendingOrBilled,
        costChangeInCents: li.commitmentLineItem?.costChangeInCents,
        owner: li.commitmentLineItem?.owner,
        displayName: li.commitmentLineItem?.projectItem.displayName ?? "",
        unbilledInCents: li.commitmentLineItem?.unbilledInCents ?? 0,
        otherBillInCents: 0,
        projectItemId: li.projectItem.id,
        taxableAmountInCents: li.taxableAmountInCents,
        isTaxExempt: li.isTaxExempt,
      })) ?? []),
    );
  }
  return { ...others, type: type.code, lineItems: formLis };
}

export function calcLineItemValues(li: ObjectState<FormLineItem>, isCredit?: boolean) {
  const {
    pendingBilledInCents: { value: pendingBilledInCents = 0 },
    costChangeInCents: { value: costChangeInCents = 0 },
    isPendingOrBilled: { value: isPendingOrBilled = true },
  } = li;

  const sign = isCredit ? -1 : 1;
  const amountInCents = isPendingOrBilled ? Number(li.amountInCents.value) || 0 : 0;
  const amountInCentsOriginal = isPendingOrBilled ? Number(li.amountInCents.originalValue) || 0 : 0;
  // We do not want to include this bill amount in the "billedInCents" column, so extract this amount out.
  const otherBilledInCents = Number(pendingBilledInCents) - amountInCentsOriginal * sign;
  return {
    otherBilledInCents,
    thisBilledInCents: Number(amountInCents),
    unBilledInCents: Number(costChangeInCents) - (otherBilledInCents + Number(amountInCents) * sign),
  };
}
